import React, { useState } from 'react';
import { useFinanceStore } from '../api/useFinanceStore';
import { Plus, X, Target, TrendingUp, Minus, RefreshCw, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';
import { formatZAR } from '../../../utils/formatters';
import DocumentActionsBar from './DocumentActionsBar';
import { generateBudgetPdf } from '../../../services/pdfService';

const BudgetManager: React.FC = () => {
    const { budgets, addBudget, variances, period, budgetsError, refreshData } = useFinanceStore();
    const [showForm, setShowForm] = useState(false);
    const [category, setCategory] = useState('');
    const [budgeted, setBudgeted] = useState('');
    const [expandedBudget, setExpandedBudget] = useState<string | null>(null);
    const [budgetPdf, setBudgetPdf] = useState<Blob>(new Blob());
    const [allBudgetsPdf, setAllBudgetsPdf] = useState<Blob>(new Blob());
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    const handleAdd = async () => {
        if (!category || !budgeted) {return;}
        await addBudget({
            category,
            budgeted: parseFloat(budgeted),
            period,
        });
        setCategory('');
        setBudgeted('');
        setShowForm(false);
    };

    const statusColor = (s: string) => {
        if (s === 'critical') {return 'bg-red-100 text-red-600';}
        if (s === 'warning') {return 'bg-amber-100 text-amber-600';}
        return 'bg-green-100 text-green-600';
    };

    const statusIcon = (s: string) => {
        if (s === 'critical') {return <TrendingDown size={12} />;}
        if (s === 'warning') {return <Minus size={12} />;}
        return <TrendingUp size={12} />;
    };

    const handleGenerateSinglePdf = async (budgetId: string) => {
        setIsGeneratingPdf(true);
        try {
            const b = budgets.find(x => x.id === budgetId);
            if (!b) { 
                setBudgetPdf(new Blob());
                return;
            }
            const variance = variances.find(v => v.category === b.category);
            const rows = [{
                id: b.id,
                category: b.category,
                period: b.period,
                budgeted: b.budgeted,
                actual: variance?.actual ?? b.actual,
                variance: variance?.variance ?? b.variance,
                status: variance?.status ?? b.status,
            }];
            const totalBudgeted = b.budgeted;
            const totalActual = variance?.actual ?? b.actual;
            const pdf = await generateBudgetPdf(rows, b.period, totalBudgeted, totalActual);
            setBudgetPdf(pdf);
        } catch (error) {
            console.error('Error generating budget PDF:', error);
            setBudgetPdf(new Blob());
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const handleGenerateAllPdf = async () => {
        setIsGeneratingPdf(true);
        try {
            const rows = budgets.map(b => {
                const v = variances.find(vr => vr.category === b.category);
                return {
                    id: b.id,
                    category: b.category,
                    period: b.period,
                    budgeted: b.budgeted,
                    actual: v?.actual ?? b.actual,
                    variance: v?.variance ?? b.variance,
                    status: v?.status ?? b.status,
                };
            });
            const totalBudgeted = budgets.reduce((s, b) => s + b.budgeted, 0);
            const totalActual = budgets.reduce((s, b) => {
                const v = variances.find(vr => vr.category === b.category);
                return s + (v?.actual ?? b.actual);
            }, 0);
            const pdf = await generateBudgetPdf(rows, period, totalBudgeted, totalActual);
            setAllBudgetsPdf(pdf);
        } catch (error) {
            console.error('Error generating all budgets PDF:', error);
            setAllBudgetsPdf(new Blob());
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    // Generate initial PDFs on mount
    React.useEffect(() => {
        if (budgets.length > 0) {
            handleGenerateAllPdf();
        }
    }, [budgets.length, period]);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-bold text-slate-800 uppercase tracking-tight">Budget Manager</h3>
                    <p className="text-xs text-slate-400 mt-1">Period: {period}</p>
                </div>
                <div className="flex gap-2">
                    {budgets.length > 0 && (
                        <DocumentActionsBar
                            kind="budget"
                            reference={`all-${period}`}
                            pdf={allBudgetsPdf}
                            documentLabel={`Budget Plan — ${period}`}
                            compact
                            onAfterAction={() => handleGenerateAllPdf()} // Regenerate after action if needed
                        />
                    )}
                    <button
                        onClick={() => setShowForm(true)}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all active:scale-95"
                    >
                        <Plus size={14} /> Add Budget
                    </button>
                </div>
            </div>

            {showForm && (
                <div className="p-4 bg-blue-50 rounded-2xl border border-blue-200 space-y-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-black text-blue-800 uppercase tracking-widest">New Budget Entry</div>
                        <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                            type="text"
                            placeholder="Category (e.g. Marketing)"
                            value={category}
                            onChange={e => setCategory(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400"
                        />
                        <input
                            type="number"
                            placeholder="Budgeted Amount (ZAR)"
                            value={budgeted}
                            onChange={e => setBudgeted(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400"
                        />
                    </div>
                    <button
                        onClick={handleAdd}
                        disabled={!category || !budgeted}
                        className="w-full py-2 bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all disabled:opacity-50"
                    >
                        Save Budget
                    </button>
                </div>
            )}

            {budgetsError && (
                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200 flex items-center justify-between">
                    <div>
                        <div className="text-amber-700 font-bold text-xs">Budgets Tab Sync Issue</div>
                        <div className="text-amber-600 text-xs mt-1">{budgetsError}</div>
                        <div className="text-amber-500 text-[10px] mt-1">Budgets you add will be saved locally and synced when the server is available.</div>
                    </div>
                    <button
                        onClick={() => refreshData()}
                        className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 transition-all flex items-center gap-1"
                    >
                        <RefreshCw size={12} /> Retry
                    </button>
                </div>
            )}

            <div className="grid gap-4">
                {budgets.length === 0 && !budgetsError ? (
                    <div className="p-10 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-300 text-slate-400 font-medium">
                        <Target size={24} className="mx-auto mb-2 text-slate-300" />
                        No budgets for this period. Add one to start tracking variances.
                    </div>
                ) : (
                    budgets.map(b => {
                        const variance = variances.find(v => v.category === b.category);
                        return (
                            <div key={b.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden group hover:border-blue-300 transition-all">
                                <div className="p-4 flex items-center justify-between">
                                    <div>
                                        <div className="font-bold text-slate-800">{b.category}</div>
                                        <div className="text-xs text-slate-500">Period: {b.period}</div>
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <div className="text-right">
                                            <div className="text-sm font-black text-slate-800">{formatZAR(b.budgeted)}</div>
                                            <div className="text-[10px] text-slate-400">Budgeted</div>
                                        </div>
                                        {variance && (
                                            <div className="text-right">
                                                <div className="text-sm font-black text-slate-800">{formatZAR(variance.actual)}</div>
                                                <div className="text-[10px] text-slate-400">Actual</div>
                                            </div>
                                        )}
                                        {variance && (
                                            <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1 ${statusColor(variance.status)}`}>
                                                {statusIcon(variance.status)}
                                                {variance.variance > 0 ? '+' : ''}{formatZAR(variance.variance)}
                                            </div>
                                        )}
                                        <button
                                            onClick={() => {
                                                setExpandedBudget(expandedBudget === b.id ? null : b.id);
                                                handleGenerateSinglePdf(b.id);
                                            }}
                                            className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                                            title="PDF actions"
                                        >
                                            {expandedBudget === b.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                        </button>
                                    </div>
                                </div>
                                {expandedBudget === b.id && (
                                    <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                                        <DocumentActionsBar
                                            kind="budget"
                                            reference={b.id}
                                            pdf={budgetPdf}
                                            documentLabel={`Budget: ${b.category} — ${b.period}`}
                                            compact
                                            onAfterAction={() => handleGenerateSinglePdf(b.id)}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default BudgetManager;